import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Key as KeyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { fetchLearnerRepository, fetchTracks, saveLearnerRepository } from '../../api/curriculumApi';
import GlossaryTerm from '../common/GlossaryTerm';
import CopyValueButton from '../common/CopyValueButton';
import { LEARNING_PATHS as ALL_LEARNING_PATHS } from '../../constants/learningPaths';

// Fallback options shown before /curriculum/tracks resolves, sourced from the
// single learning-paths source of truth (src/constants/learningPaths.js).
// Only includes paths that have a real track (learningPathKey set).
const FALLBACK_PATHS = ALL_LEARNING_PATHS
  .filter((path) => path.learningPathKey)
  .map((path) => ({
    learning_path_key: path.learningPathKey,
    title: path.jobReadyLabel,
    slug: path.route ? path.route.replace('/roadmaps/', '') : path.id,
  }));

const normalizeTracks = (tracks = []) => {
  const byKey = new Map();
  [...tracks, ...FALLBACK_PATHS].forEach((track) => {
    const key = track.learning_path_key || track.slug;
    if (!key || byKey.has(key)) return;
    byKey.set(key, {
      key,
      label: track.title || track.name || track.label || key,
      slug: track.slug || key,
    });
  });
  return Array.from(byKey.values());
};

const GitHubSettingsPanel = ({ framed = true }) => {
  const theme = useTheme();
  const [learningPaths, setLearningPaths] = useState(normalizeTracks());
  const [learningPathKey, setLearningPathKey] = useState('data-analyst');
  const [repo, setRepo] = useState({
    repo_url: '',
    default_branch: 'main',
    github_pat: '',
    has_github_pat: false,
    fallback_learning_path_key: '',
  });
  const [editingPat, setEditingPat] = useState(false);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const tokenName = `${learningPathKey}-qode-clarity-PAT`;
  const tokenDescription = 'Allows Qode Clarity to update journal.md and the README journal link in my selected learning repo.';

  useEffect(() => {
    fetchTracks()
      .then((tracks) => setLearningPaths(normalizeTracks(tracks)))
      .catch(() => setLearningPaths(normalizeTracks()));
  }, []);

  useEffect(() => {
    setStatus('');
    setRepo({
      repo_url: '',
      default_branch: 'main',
      github_pat: '',
      has_github_pat: false,
      fallback_learning_path_key: '',
    });
    fetchLearnerRepository(learningPathKey)
      .then((data) => {
        setRepo({
          repo_url: data.repo_url || '',
          default_branch: data.default_branch || 'main',
          github_pat: '',
          has_github_pat: Boolean(data.has_github_pat),
          fallback_learning_path_key: data.fallback_learning_path_key || '',
        });
        setEditingPat(!data.has_github_pat);
      })
      .catch((error) => setStatus(error.message));
  }, [learningPathKey]);

  const saveSettings = async () => {
    setSaving(true);
    setStatus('');
    try {
      const saved = await saveLearnerRepository(learningPathKey, {
        repo_url: repo.repo_url,
        local_path: '',
        default_branch: repo.default_branch || 'main',
        setup_status: repo.repo_url ? 'ready' : 'not_started',
        setup_checklist: {},
        github_pat: repo.github_pat,
      });
      setRepo((current) => ({
        ...current,
        github_pat: '',
        has_github_pat: Boolean(saved.has_github_pat),
        fallback_learning_path_key: saved.fallback_learning_path_key || '',
      }));
      setEditingPat(!saved.has_github_pat);
      setStatus('GitHub settings saved.');
      window.dispatchEvent(new CustomEvent('github-settings-updated', { detail: saved }));
    } catch (error) {
      setStatus(error.message || 'Could not save GitHub settings.');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <GitHubIcon />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            GitHub journal sync
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a track, then connect the <GlossaryTerm term="repo">repo</GlossaryTerm> Qode Clarity should use
            for that track's journals and materials.
          </Typography>
        </Box>
      </Stack>

      <FormControl fullWidth>
        <InputLabel id="github-learning-path-label">Track</InputLabel>
        <Select
          labelId="github-learning-path-label"
          label="Track"
          value={learningPathKey}
          onChange={(event) => setLearningPathKey(event.target.value)}
        >
          {learningPaths.map((path) => (
            <MenuItem key={path.key} value={path.key}>
              {path.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {repo.fallback_learning_path_key && (
        <Alert severity="info">
          You haven't set up a GitHub repo for this track yet, so it's using your Data Analyst repo for now.
          Saving here gives this track its own separate repo instead.
        </Alert>
      )}

      <TextField
        label="GitHub repo URL"
        value={repo.repo_url}
        onChange={(event) => setRepo((current) => ({ ...current, repo_url: event.target.value }))}
        placeholder={`https://github.com/you/${learningPathKey}`}
        fullWidth
      />

      <TextField
        label="Default branch"
        value={repo.default_branch}
        onChange={(event) => setRepo((current) => ({ ...current, default_branch: event.target.value }))}
        placeholder="main"
        fullWidth
      />

      <TextField
        label={
          repo.has_github_pat
            ? <>Replace <GlossaryTerm term="github pat">GitHub PAT</GlossaryTerm></>
            : <GlossaryTerm term="github pat">GitHub PAT</GlossaryTerm>
        }
        value={repo.has_github_pat && !editingPat ? '••••••••••••••••••••••••••••••••' : repo.github_pat}
        onFocus={() => {
          if (repo.has_github_pat && !editingPat) {
            setEditingPat(true);
            setRepo((current) => ({ ...current, github_pat: '' }));
            setStatus('Cleared the field so you can enter a new token — click "Keep saved token" below if you didn\'t mean to replace it.');
          }
        }}
        onChange={(event) => setRepo((current) => ({ ...current, github_pat: event.target.value }))}
        placeholder={repo.has_github_pat ? 'Leave blank to keep current token' : 'Fine-grained token with Contents read/write permission'}
        type="password"
        fullWidth
        helperText={repo.has_github_pat ? 'A token is already saved. Clicking this field clears it so you can enter a new one — use "Keep saved token" below to back out without losing it.' : 'Use a fine-grained token scoped to this repo with Contents read/write permission.'}
      />
      {repo.has_github_pat && editingPat && (
        <Button
          size="small"
          variant="text"
          onClick={() => {
            setEditingPat(false);
            setRepo((current) => ({ ...current, github_pat: '' }));
            setStatus('');
          }}
          sx={{ alignSelf: 'flex-start', mt: -1.5, textTransform: 'none' }}
        >
          Keep saved token
        </Button>
      )}

      <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          <KeyIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            How to get a GitHub PAT
          </Typography>
        </Stack>
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
            Use these exact example values on GitHub:
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              Token name: {tokenName}
            </Typography>
            <CopyValueButton value={tokenName} label="token name" onCopyError={() => setStatus('Could not copy automatically. Select the text and copy it manually.')} />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              Description: {tokenDescription}
            </Typography>
            <CopyValueButton value={tokenDescription} label="description" onCopyError={() => setStatus('Could not copy automatically. Select the text and copy it manually.')} />
          </Stack>
        </Box>
        <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            Open{' '}
            <Link href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noopener noreferrer">
              GitHub fine-grained tokens
            </Link>
            .
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            In <strong>Token name</strong>, enter <strong>{tokenName}</strong>. In <strong>Description</strong>, paste the description shown above.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Set <strong>Resource owner</strong> to the account or organisation that owns your learning repo.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Set <strong>Expiration</strong> to 30 or 90 days. If it expires later, create a new token and replace it here.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Under <strong>Repository access</strong>, choose <strong>Only select repositories</strong>, then select only your learning repo.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Under <strong>Permissions</strong>, open <strong>Repository permissions</strong>, add <strong>Contents</strong>, and set it to <strong>Read and write</strong>. Leave <strong>Metadata</strong> as read-only.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Click <strong>Generate token</strong>, copy it once, paste it into the GitHub PAT field above, then save. GitHub will not show it again.
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Do not paste a full-account classic token. Use a fine-grained token limited to this repo.
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={saveSettings}
        disabled={saving}
        sx={{ alignSelf: 'flex-start' }}
      >
        {saving ? 'Saving...' : 'Save GitHub settings'}
      </Button>

      {status && (
        <Alert severity={status.includes('saved') ? 'success' : 'info'}>
          {status}
        </Alert>
      )}
    </Stack>
  );

  if (!framed) return content;
  return <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>{content}</Paper>;
};

export default GitHubSettingsPanel;
